"use client";

import React, { useEffect, useState } from "react";
import { Plus, MapPin, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { StandardFormInput } from "@/components/common/StandardFormInput";
import {
  ButtonPrimary,
  ButtonOutline,
} from "@/components/common/StandardButton";
import StandardCard from "@/components/common/StandardCard";
import { CreateCustomerAddressDto } from "@/types/models/customer";
import { useForm, SubmitHandler } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "@/store/store";
import {
  fetchCustomerAddressesRequest,
  createCustomerAddressRequest,
  deleteCustomerAddressRequest,
  resetAddressFormState,
} from "@/store/slices/customer.slice";
import { StateCitySelector } from "@/components/common/StateCitySelector";

interface CustomerAddressCardProps {
  customerId: string;
}

// Form validation schema
const addressSchema = z.object({
  address_line_one: z.string().min(1, "Address Line 1 is required"),
  address_line_two: z.string().optional(),
  city: z.string().min(1, "City is required"),
  state: z.string().min(1, "State is required"),
  postal_code: z.string().min(1, "Postal code is required"),
  billing_address: z.boolean().optional(),
});

const CustomerAddressCard: React.FC<CustomerAddressCardProps> = ({
  customerId,
}) => {
  const dispatch = useDispatch();
  const [showAddForm, setShowAddForm] = useState(false);

  // Redux state
  const { addresses, addressesLoading, addressesError, addressFormState } =
    useSelector((state: RootState) => state.customers);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<CreateCustomerAddressDto>({
    resolver: zodResolver(addressSchema),
    mode: "onSubmit",
  });

  // Watch state and city values for the form
  const selectedState = watch("state");
  const selectedCity = watch("city");

  // Fetch addresses on mount
  useEffect(() => {
    if (customerId) {
      dispatch(fetchCustomerAddressesRequest({ customerId }));
    }
  }, [customerId, dispatch]);

  // Handle form success
  useEffect(() => {
    if (addressFormState.success) {
      reset();
      setShowAddForm(false);
      dispatch(resetAddressFormState());
    }
  }, [addressFormState.success, reset, dispatch]);

  const onSubmit: SubmitHandler<CreateCustomerAddressDto> = (data) => {
    dispatch(createCustomerAddressRequest({ customerId, address: data }));
  };

  const handleDeleteAddress = (addressId: string) => {
    if (confirm("Are you sure you want to delete this address?")) {
      dispatch(deleteCustomerAddressRequest({ customerId, addressId }));
    }
  };

  const handleCancelAdd = () => {
    setShowAddForm(false);
    reset();
    dispatch(resetAddressFormState());
  };

  return (
    <StandardCard>
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MapPin className="h-4 w-4 text-primary" />
            <h4 className="text-sm font-medium text-primary uppercase tracking-wider">
              Customer Addresses
            </h4>
          </div>
          {!showAddForm && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowAddForm(true)}
              className="h-7 px-2 text-xs"
            >
              <Plus className="h-3 w-3 mr-1" />
              Add Address
            </Button>
          )}
        </div>

        {/* Add Address Form */}
        {showAddForm && (
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="space-y-3 p-3 bg-muted/30 rounded-lg border"
          >
            <StandardFormInput
              label="Address Line 1"
              placeholder="Enter address line 1"
              {...register("address_line_one")}
              error={errors.address_line_one?.message}
              required
            />
            <StandardFormInput
              label="Address Line 2"
              placeholder="Enter address line 2 (optional)"
              {...register("address_line_two")}
              error={errors.address_line_two?.message}
            />
            <StateCitySelector
              selectedState={selectedState}
              selectedCity={selectedCity}
              onStateChange={(state) => setValue("state", state)}
              onCityChange={(city) => setValue("city", city)}
              stateError={errors.state?.message}
              cityError={errors.city?.message}
              stateHasError={!!errors.state}
              cityHasError={!!errors.city}
              required={true}
            />
            <StandardFormInput
              label="Postal Code"
              placeholder="Enter postal code"
              {...register("postal_code")}
              error={errors.postal_code?.message}
              required
            />
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="billing_address"
                {...register("billing_address")}
                className="h-4 w-4 text-primary border-gray-300 rounded focus:ring-primary"
              />
              <label
                htmlFor="billing_address"
                className="text-sm text-foreground"
              >
                Use as billing address
              </label>
            </div>
            <div className="flex items-center gap-2 justify-end">
              <ButtonOutline
                type="button"
                size="sm"
                onClick={handleCancelAdd}
                disabled={addressFormState.loading}
              >
                Cancel
              </ButtonOutline>
              <ButtonPrimary
                type="submit"
                size="sm"
                disabled={addressFormState.loading}
              >
                {addressFormState.loading ? "Adding..." : "Add Address"}
              </ButtonPrimary>
            </div>
            {addressFormState.error && (
              <div className="text-sm text-destructive">
                {addressFormState.error.message}
              </div>
            )}
          </form>
        )}

        {/* Addresses List */}
        <div className="space-y-3">
          {addressesLoading ? (
            <div className="space-y-3">
              {[1, 2].map((i) => (
                <div key={i} className="animate-pulse">
                  <div className="h-4 bg-muted rounded w-32 mb-2"></div>
                  <div className="h-3 bg-muted rounded w-full mb-1"></div>
                  <div className="h-3 bg-muted rounded w-20"></div>
                </div>
              ))}
            </div>
          ) : addressesError ? (
            <div className="text-center text-destructive text-sm">
              {addressesError.message}
            </div>
          ) : addresses.length === 0 ? (
            <div className="text-center text-muted-foreground text-sm py-4">
              No addresses found for this customer
            </div>
          ) : (
            addresses.map((address) => (
              <div
                key={address.id}
                className="p-3 bg-muted/20 rounded-lg border space-y-2"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-2">
                    <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-foreground">
                          {address.address_line_one}
                        </span>
                        {address.billing_address && (
                          <span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded-full">
                            Billing
                          </span>
                        )}
                      </div>
                      {address.address_line_two && (
                        <p className="text-sm text-muted-foreground">
                          {address.address_line_two}
                        </p>
                      )}
                      <p className="text-sm text-muted-foreground">
                        {address.city}, {address.state} - {address.postal_code}
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteAddress(address.id)}
                    className="h-6 w-6 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </StandardCard>
  );
};

export default CustomerAddressCard;
